import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMessageBox, updateMessageBox , setPageSize} from '../../slices/messageBoxSlice';
import Loader from "../../components/Loader";
import EditableTable from './SurveyHistoryTable';
import Modal from '../../components/Modal';
import AddSms from "./AddSms";
import SimplePagination from "@/components/SimplePagination";
import PanelUserSearch from "@/components/PanelUserSearch";
import PageSizeSelector from "@/components/PageSizeSelector";
import UpdateSmsBox from "./UpdateSmsBox";

const SmsBox = () => {
  const dispatch = useDispatch();

  // Redux state
  const { messageBoxList, status, error, search, pageSize  } = useSelector((state) => state.smsBox);

  // Local state
  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
  const [editRowId, setEditRowId] = useState(null); // Track which row is being edited
  const [editedData, setEditedData] = useState({}); // Temp data for edited row
  const [searchInput, setSearchInput] = useState(search || ""); // Search input for debounced search
  const [isModalOpen, setIsModalOpen] = useState(false); // Send SMS modal

  const [selectedRow, setSelectedRow] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  // Editable fields for table
  const editableFields = ['is_read', 'body', 'title', 'status'];

  // Determine headers dynamically
  const headers = messageBoxList?.result?.length > 0
    ? Object.keys(messageBoxList.result[0]).filter((key) => key !== 'id')
    : [];

  // Check if data exists
  const hasData = messageBoxList?.result?.length > 0;

  // Calculate total pages
  const totalPages = messageBoxList?.pagination
  ? Math.ceil((messageBoxList.pagination.count || 0) / pageSize)
  : 0;

  // =======================
  // Fetch message box on mount
  // =======================
  useEffect(() => {
    dispatch(fetchMessageBox({ page: 1, search }));
  }, [dispatch]);

  // =======================
  // Sync search input from Redux store
  // =======================
  useEffect(() => {
    setSearchInput(search || "");
  }, [search]);

  // =======================
  // Debounced search
  // =======================
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchMessageBox({ page: 1, search: searchInput }));
    }, 800);

    return () => clearTimeout(timer);
  }, [searchInput, dispatch]);

  // =======================
  // Page change effect
  // =======================
  useEffect(() => {
    dispatch(fetchMessageBox({ page: currentPage, search }));
  }, [currentPage, dispatch]);

  // =======================
  // Handlers
  // =======================

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // const handleEdit = (rowId) => {
  //   setEditRowId(rowId);
  //   const rowData = messageBoxList.result?.find((item) => item.id === rowId);
  //   setEditedData(rowData || {});
  // };

  const handleEdit = (rowId) => {
  const rowData = messageBoxList.result?.find((item) => item.id === rowId);

  setSelectedRow(rowData);
  setIsUpdateModalOpen(true);
};

  const handleSave = async () => {
    const { id, title, body, is_read, status } = editedData;
    const updatedData = { smsList: [{ id, title, body, is_read, status }] };

    const resp = await dispatch(updateMessageBox(updatedData));
    if (!resp.error) {
      setEditRowId(null);
      // ✅ Fetch fresh data
    dispatch(fetchMessageBox({ page: 1, search: searchInput }));
    }
  };

  const handleCancel = () => {
    setEditRowId(null);
    setEditedData({});
  };

  const handleChange = (e, field, val) => {
    if (['status', 'is_read'].includes(field)) {
      setEditedData((prev) => ({ ...prev, [field]: !val }));
    } else {
      setEditedData((prev) => ({ ...prev, [field]: e.target.value }));
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePageSizeChange = (size) => {
  dispatch(setPageSize(size));         // update pageSize in slice
  dispatch(fetchMessageBox({ page: 1, search, pageSize: size }));  // fetch first page with new pageSize
};

  // =======================
  // Loading state
  // =======================
  if (status === 'loading') {
    return (
      <div className="w-full pt-20 flex justify-center">
        <Loader />
      </div>
    );
  }

  // =======================
  // No data / empty state
  // =======================
  if (!hasData) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-20 text-gray-500">
        <PanelUserSearch value={searchInput} onChange={setSearchInput} />
        <div className="text-5xl mb-3">🔍</div>
        {searchInput ? (
          <>
            <p className="text-lg font-medium">No results found</p>
            <p className="text-sm mt-1">
              for "<span className="font-semibold">{searchInput}</span>"
            </p>
          </>
        ) : (
          <>
            <p className="text-lg font-medium">No Data Available</p>
            <p className="text-sm mt-1">There are no SMS messages to display</p>
          </>
        )}
      </div>
    );
  }

  // =======================
  // Render main table
  // =======================
  return (
    <div className="w-full flex items-center flex-col">

      {/* Search box */}
      <PanelUserSearch value={searchInput} onChange={setSearchInput} />

      {/* Editable table */}
      <div className="max-w-full mt-4 overflow-scroll">
        <EditableTable
          headers={headers}
          rows={messageBoxList.result || []}
          editRowId={editRowId}
          editedData={editedData}
          handleEdit={handleEdit}
          handleSave={handleSave}
          handleCancel={handleCancel}
          handleChange={handleChange}
          editableFields={editableFields}
          hideChangeLog={true}
        />
      </div>

      {/* Pagination & Page size selector */}
      <div className="flex mt-4 items-center justify-between w-11/12">
        <PageSizeSelector pageSize={pageSize} setPageSize={handlePageSizeChange} />
        <SimplePagination
          currentPage={currentPage}
          setCurrentPage={handlePageChange}
          totalPages={totalPages}
        />
        <button
          className="activeBtn text-sm text-white py-1 px-2 rounded"
          onClick={openModal}
        >
          Send Messages
        </button>
      </div>

      {/* Send SMS Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title="Send Messages"
        content={<AddSms onClose={closeModal} />}
      />

      <Modal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        title="Update Messages"
        content={
          <UpdateSmsBox
            data={selectedRow}
            onClose={() => setIsUpdateModalOpen(false)}
          />
        }
      />
    </div>
  );
};

export default SmsBox;